import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { videoAPI, staffAPI, aiAPI, visitSummaryAPI, patientChartAPI } from '../../lib/api';
import api from '../../lib/api';
import { 
  VideoCamera,
  Phone,
  PhoneDisconnect,
  Microphone,
  MicrophoneSlash,
  Monitor,
  Record,
  Stop,
  ChatCircle,
  Users,
  Plus,
  X,
  Check,
  Clock,
  PaperPlaneTilt,
  ArrowClockwise,
  UserCircle,
  Calendar,
  NotePencil,
  FileText,
  Waveform,
  PencilSimple,
  EnvelopeSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import DailyIframe from '@daily-co/daily-js';

export default function VideoCallsPage() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rooms'); // rooms, history, create
  
  // Active call state
  const [activeCall, setActiveCall] = useState(null);
  const [callFrame, setCallFrame] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  
  // Waiting room state
  const [waitingUsers, setWaitingUsers] = useState([]);
  
  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  // Live transcription state
  const [showTranscription, setShowTranscription] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [clinicalNotes, setClinicalNotes] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const transcriptionIntervalRef = useRef(null);
  
  // Visit Summary Preview state
  const [showSummaryPreview, setShowSummaryPreview] = useState(false);
  const [summaryData, setSummaryData] = useState({
    reason_for_visit: '',
    vitals: null,
    assessment: '',
    prescriptions: [],
    lab_orders: '',
    imaging_orders: '',
    referrals: '',
    followup_instructions: '',
    additional_notes: ''
  });
  const [sendingSummary, setSendingSummary] = useState(false);
  const [patientInfo, setPatientInfo] = useState(null);
  
  // Create room state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [patients, setPatients] = useState([]);
  const [newRoom, setNewRoom] = useState({
    room_name: '',
    room_type: 'telehealth',
    participant_ids: [],
    enable_recording: true,
    waiting_room_enabled: true,
    scheduled_time: ''
  });

  const videoContainerRef = useRef(null);
  const chatIntervalRef = useRef(null);
  const waitingIntervalRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [roomsRes, historyRes] = await Promise.all([
        videoAPI.getRooms(),
        videoAPI.getHistory()
      ]);
      setRooms(roomsRes.data.rooms || []);
      setHistory(historyRes.data.history || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Error loading video data:', error);
      }
      toast.error('Failed to load video calls');
    } finally {
      setLoading(false);
    }
  }, []); // videoAPI is module-level, toast is external - no dependencies needed

  useEffect(() => {
    loadData();
    return () => {
      if (chatIntervalRef.current) clearInterval(chatIntervalRef.current);
      if (waitingIntervalRef.current) clearInterval(waitingIntervalRef.current);
    };
  }, [loadData]);

  const loadStaffAndPatients = async () => {
    try {
      const [staffRes, patientsRes] = await Promise.all([
        staffAPI.getAll(),
        api.get('/patients')
      ]);
      setStaffList((staffRes.data.staff || []).filter(s => s.id !== user?.id));
      setPatients(patientsRes.data.patients || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Error loading users:', error);
    }
  };

  const createRoom = async () => {
    if (!newRoom.room_name) {
      toast.error('Please enter a room name');
      return;
    }
    
    try {
      const response = await videoAPI.createRoom(newRoom);
      toast.success('Room created successfully');
      setShowCreateModal(false);
      setNewRoom({
        room_name: '',
        room_type: 'telehealth',
        participant_ids: [],
        enable_recording: true,
        waiting_room_enabled: true,
        scheduled_time: ''
      });
      loadData();
      
      // Optionally join the room immediately
      if (window.confirm('Do you want to join the room now?')) {
        joinRoom(response.data);
      }
    } catch (error) {
      toast.error('Failed to create room');
      process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error(error);
    }
  };

  const joinRoom = async (room) => {
    try {
      const response = await videoAPI.joinRoom(room.id);
      
      if (response.data.status === 'waiting') {
        toast.info('You are in the waiting room. Please wait to be admitted.');
        return;
      }
      
      // Join the call with Daily.co URL and token
      const dailyUrl = response.data.daily_room_url || room.daily_room_url;
      const meetingToken = response.data.meeting_token;
      
      setActiveCall({
        ...room,
        daily_room_url: dailyUrl,
        meeting_token: meetingToken
      });
      
      initializeVideoCall(dailyUrl, meetingToken);
      
      // Start polling for chat and waiting room
      startPolling(room.id);
    } catch (error) {
      toast.error('Failed to join room');
      process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error(error);
    }
  };

  const initializeVideoCall = (dailyUrl, meetingToken) => {
    if (videoContainerRef.current && dailyUrl) {
      // Destroy existing frame if any
      if (callFrame) {
        callFrame.destroy();
      }
      
      // Create real Daily.co iframe
      try {
        const frame = DailyIframe.createFrame(videoContainerRef.current, {
          iframeStyle: {
            width: '100%',
            height: '100%',
            border: '0',
            borderRadius: '12px',
          },
          showLeaveButton: false,
          showFullscreenButton: true,
        });
        
        setCallFrame(frame);
        
        // Join the room with token
        frame.join({
          url: dailyUrl,
          token: meetingToken,
        }).then(() => {
          process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.log('Joined Daily.co room successfully');
          toast.success('Connected to video call');
        }).catch((err) => {
          process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Failed to join Daily.co room:', err);
          toast.error('Failed to connect to video call');
        });
        
        // Set up event listeners
        frame.on('left-meeting', () => {
          leaveCall();
        });
        
        frame.on('participant-joined', (event) => {
          process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.log('Participant joined:', event.participant);
        });
        
        frame.on('participant-left', (event) => {
          process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.log('Participant left:', event.participant);
        });
        
        frame.on('error', (event) => {
          process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Daily.co error:', event);
          toast.error('Video call error occurred');
        });
        
      } catch (error) {
        process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Error creating Daily frame:', error);
        // Fallback to mock UI if Daily.co fails
        createMockVideoUI();
      }
    } else if (videoContainerRef.current) {
      // No Daily URL - use mock UI
      createMockVideoUI();
    }
  };

  const createMockVideoUI = () => {
    if (videoContainerRef.current) {
      // Clear container safely
      while (videoContainerRef.current.firstChild) {
        videoContainerRef.current.removeChild(videoContainerRef.current.firstChild);
      }
      
      // Create elements safely using DOM APIs instead of innerHTML (XSS prevention)
      const mockFrame = document.createElement('div');
      mockFrame.className = 'mock-video-frame';
      
      const videoGrid = document.createElement('div');
      videoGrid.className = 'video-grid';
      
      // Local video tile
      const localTile = document.createElement('div');
      localTile.className = 'video-tile local';
      const localPlaceholder = document.createElement('div');
      localPlaceholder.className = 'video-placeholder';
      
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '64');
      svg.setAttribute('height', '64');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'currentColor');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z');
      svg.appendChild(path);
      
      const youLabel = document.createElement('span');
      youLabel.textContent = 'You';
      
      localPlaceholder.appendChild(svg);
      localPlaceholder.appendChild(youLabel);
      localTile.appendChild(localPlaceholder);
      
      // Remote video tile
      const remoteTile = document.createElement('div');
      remoteTile.className = 'video-tile remote';
      const remotePlaceholder = document.createElement('div');
      remotePlaceholder.className = 'video-placeholder waiting';
      const waitingLabel = document.createElement('span');
      waitingLabel.textContent = 'Waiting for others to join...';
      remotePlaceholder.appendChild(waitingLabel);
      remoteTile.appendChild(remotePlaceholder);
      
      videoGrid.appendChild(localTile);
      videoGrid.appendChild(remoteTile);
      mockFrame.appendChild(videoGrid);
      videoContainerRef.current.appendChild(mockFrame);
    }
  };

  const startPolling = (roomId) => {
    // Poll for chat messages
    chatIntervalRef.current = setInterval(async () => {
      try {
        const response = await videoAPI.getChat(roomId);
        setChatMessages(response.data.messages || []);
      } catch (error) {
        process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Error polling chat:', error);
      }
    }, 3000);

    // Poll for waiting room (staff only)
    if (user?.role !== 'patient') {
      waitingIntervalRef.current = setInterval(async () => {
        try {
          const response = await videoAPI.getWaitingRoom(roomId);
          setWaitingUsers(response.data.waiting || []);
        } catch (error) {
          process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Error polling waiting room:', error);
        }
      }, 5000);
    }
  };

  const leaveCall = async () => {
    // Destroy Daily.co frame if it exists
    if (callFrame) {
      try {
        await callFrame.leave();
        callFrame.destroy();
      } catch (error) {
        process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Error destroying Daily frame:', error);
      }
      setCallFrame(null);
    }
    
    if (activeCall) {
      try {
        await videoAPI.leaveRoom(activeCall.id);
      } catch (error) {
        process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Error leaving room:', error);
      }
    }
    
    // Clean up
    if (chatIntervalRef.current) clearInterval(chatIntervalRef.current);
    if (waitingIntervalRef.current) clearInterval(waitingIntervalRef.current);
    
    setActiveCall(null);
    setChatMessages([]);
    setWaitingUsers([]);
    setShowChat(false);
    loadData();
  };

  const endCall = async () => {
    if (!activeCall) return;
    
    // For telehealth calls, show summary preview instead of auto-sending
    if (activeCall.type === 'telehealth' && activeCall.patient_id && (user.role === 'provider' || user.role === 'cma')) {
      // Stop transcription if running
      if (isTranscribing) {
        stopLiveTranscription();
      }
      
      // Prepare summary data
      try {
        // Get patient info for the summary
        const chartRes = await patientChartAPI.getChart(activeCall.patient_id);
        const patient = chartRes.data.patient;
        setPatientInfo(patient);
        
        // Get latest intake for reason for visit and vitals
        const latestIntake = chartRes.data.intakes?.[0];
        
        setSummaryData({
          reason_for_visit: latestIntake?.chief_complaint || '',
          vitals: latestIntake?.vital_signs || null,
          assessment: clinicalNotes || liveTranscript || '',  // Use transcribed notes
          prescriptions: [],
          lab_orders: '',
          imaging_orders: '',
          referrals: '',
          followup_instructions: '',
          additional_notes: ''
        });
        
        setShowSummaryPreview(true);
      } catch (error) {
        process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Error loading patient data:', error);
        // Still show preview but with empty data
        setShowSummaryPreview(true);
      }
      return;
    }
    
    // For non-telehealth or non-provider, just end the call
    if (!window.confirm('Are you sure you want to end the call for everyone?')) return;
    
    try {
      await videoAPI.endRoom(activeCall.id);
      toast.success('Call ended');
      leaveCall();
    } catch (error) {
      toast.error('Failed to end call');
      process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error(error);
    }
  };

  const confirmEndCallAndSendSummary = async (sendEmail = true) => {
    if (!activeCall) return;
    
    setSendingSummary(true);
    
    try {
      // First end the video room
      await videoAPI.endRoom(activeCall.id);
      
      // If sending email, create and send the summary with edited data
      if (sendEmail && patientInfo) {
        const response = await api.post('/visit-summary/send-custom', {
          patient_id: activeCall.patient_id,
          reason_for_visit: summaryData.reason_for_visit,
          vitals: summaryData.vitals,
          assessment: summaryData.assessment,
          prescriptions: summaryData.prescriptions,
          lab_orders: summaryData.lab_orders || null,
          imaging_orders: summaryData.imaging_orders || null,
          referrals: summaryData.referrals || null,
          followup_instructions: summaryData.followup_instructions || null,
          additional_notes: summaryData.additional_notes || null
        });
        
        if (response.data?.success) {
          toast.success(`Visit summary sent to ${patientInfo.email}`, {
            duration: 5000
          });
        }
      } else {
        toast.success('Call ended');
      }
      
      setShowSummaryPreview(false);
      leaveCall();
    } catch (error) {
      toast.error('Failed to end call or send summary');
      process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error(error);
    } finally {
      setSendingSummary(false);
    }
  };

  // Live transcription functions
  const startLiveTranscription = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.start(5000); // Collect audio every 5 seconds
      setIsTranscribing(true);
      setShowTranscription(true);
      
      // Set up interval to transcribe chunks
      transcriptionIntervalRef.current = setInterval(async () => {
        if (audioChunksRef.current.length > 0) {
          const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          audioChunksRef.current = []; // Clear chunks
          
          // Convert to base64 and transcribe
          const reader = new FileReader();
          reader.onload = async () => {
            const base64 = reader.result.split(',')[1];
            try {
              const response = await aiAPI.transcribe({
                audio_data: base64,
                generate_clinical_notes: false
              });
              if (response.data?.transcription) {
                setLiveTranscript(prev => prev + ' ' + response.data.transcription);
              }
            } catch (err) {
              process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Transcription error:', err);
            }
          };
          reader.readAsDataURL(blob);
        }
      }, 6000); // Transcribe every 6 seconds
      
      toast.success('Live transcription started');
    } catch (error) {
      process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Error starting transcription:', error);
      toast.error('Could not start transcription. Please check microphone permissions.');
    }
  };

  const stopLiveTranscription = async () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    
    if (transcriptionIntervalRef.current) {
      clearInterval(transcriptionIntervalRef.current);
    }
    
    setIsTranscribing(false);
    
    // Generate clinical notes from transcript if we have one
    if (liveTranscript.trim()) {
      try {
        toast.info('Generating clinical notes...');
        const response = await aiAPI.scribe({
          notes: liveTranscript,
          encounter_id: null
        });
        if (response.data?.scribe_notes) {
          setClinicalNotes(response.data.scribe_notes);
          toast.success('Clinical notes generated');
        }
      } catch (error) {
        process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Error generating notes:', error);
      }
    }
    
    toast.info('Transcription stopped');
  };

  const toggleAudio = () => {
    if (callFrame) {
      callFrame.setLocalAudio(!isAudioEnabled);
    }
    setIsAudioEnabled(!isAudioEnabled);
    toast.info(isAudioEnabled ? 'Microphone muted' : 'Microphone unmuted');
  };

  const toggleVideo = () => {
    if (callFrame) {
      callFrame.setLocalVideo(!isVideoEnabled);
    }
    setIsVideoEnabled(!isVideoEnabled);
    toast.info(isVideoEnabled ? 'Camera off' : 'Camera on');
  };

  const toggleRecording = async () => {
    if (!activeCall) return;
    
    try {
      if (callFrame && activeCall.daily_room_url) {
        // Use Daily.co native recording
        if (isRecording) {
          await callFrame.stopRecording();
        } else {
          await callFrame.startRecording();
        }
      }
      
      // Also update our backend
      if (isRecording) {
        await videoAPI.stopRecording(activeCall.id);
        toast.success('Recording stopped');
      } else {
        await videoAPI.startRecording(activeCall.id);
        toast.success('Recording started');
      }
      setIsRecording(!isRecording);
    } catch (error) {
      toast.error('Recording failed');
      process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error(error);
    }
  };

  const toggleScreenShare = async () => {
    if (callFrame) {
      try {
        if (isScreenSharing) {
          await callFrame.stopScreenShare();
        } else {
          await callFrame.startScreenShare();
        }
      } catch (error) {
        process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error('Screen share error:', error);
      }
    }
    setIsScreenSharing(!isScreenSharing);
    toast.info(isScreenSharing ? 'Screen sharing stopped' : 'Screen sharing started');
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !activeCall) return;
    
    try {
      await videoAPI.sendChat(activeCall.id, newMessage);
      setNewMessage('');
      
      // Refresh messages
      const response = await videoAPI.getChat(activeCall.id);
      setChatMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to send message');
      process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error(error);
    }
  };

  const admitUser = async (userId) => {
    if (!activeCall) return;
    
    try {
      await videoAPI.admitUser(activeCall.id, userId);
      toast.success('User admitted');
      
      // Refresh waiting room
      const response = await videoAPI.getWaitingRoom(activeCall.id);
      setWaitingUsers(response.data.waiting || []);
    } catch (error) {
      toast.error('Failed to admit user');
      process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error(error);
    }
  };

  const denyUser = async (userId) => {
    if (!activeCall) return;
    
    try {
      await videoAPI.denyUser(activeCall.id, userId);
      toast.success('User denied');
      
      // Refresh waiting room
      const response = await videoAPI.getWaitingRoom(activeCall.id);
      setWaitingUsers(response.data.waiting || []);
    } catch (error) {
      toast.error('Failed to deny user');
      process.env.NODE_ENV === "development" && process.env.NODE_ENV === "development" && console.error(error);
    }
  };

  // Active Call UI
  if (activeCall) {
    return (
      <div className="h-screen flex flex-col bg-slate-900" data-testid="active-video-call">
        {/* Call Header */}
        <div className="bg-slate-800 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <VideoCamera size={24} className="text-teal-400" />
            <div>
              <h2 className="text-white font-semibold">{activeCall.room_name}</h2>
              <p className="text-slate-400 text-sm">
                {activeCall.room_type === 'telehealth' ? 'Telehealth Consultation' : 'Staff Meeting'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isRecording && (
              <span className="flex items-center gap-1 text-red-400 text-sm">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                Recording
              </span>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Video Area */}
          <div className="flex-1 p-4" ref={videoContainerRef}>
            <style>{`
              .mock-video-frame { height: 100%; }
              .video-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; height: 100%; }
              .video-tile { background: #1e293b; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
              .video-placeholder { display: flex; flex-direction: column; align-items: center; gap: 8px; color: #94a3b8; }
              .video-placeholder svg { opacity: 0.5; }
              .video-tile.local { border: 2px solid #14b8a6; }
            `}</style>
          </div>

          {/* Sidebar - Waiting Room & Chat */}
          <div className="w-80 bg-slate-800 flex flex-col">
            {/* Waiting Room (Staff only) */}
            {user?.role !== 'patient' && waitingUsers.length > 0 && (
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Clock size={18} />
                  Waiting Room ({waitingUsers.length})
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {waitingUsers.map(waiting => (
                    <div key={waiting.id} className="bg-slate-700 rounded-lg p-2 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">{waiting.user_name}</p>
                        <p className="text-slate-400 text-xs">{waiting.user_role}</p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => admitUser(waiting.user_id)}
                          className="p-1.5 bg-green-600 hover:bg-green-700 rounded text-white"
                          title="Admit"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => denyUser(waiting.user_id)}
                          className="p-1.5 bg-red-600 hover:bg-red-700 rounded text-white"
                          title="Deny"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Chat */}
            <div className="flex-1 flex flex-col">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <ChatCircle size={18} />
                  Chat
                </h3>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {chatMessages.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center">No messages yet</p>
                ) : (
                  chatMessages.map(msg => (
                    <div key={msg.id} className={`p-2 rounded-lg ${msg.sender_id === user?.id ? 'bg-teal-600 ml-8' : 'bg-slate-700 mr-8'}`}>
                      <p className="text-xs text-slate-300">{msg.sender_name}</p>
                      <p className="text-white text-sm">{msg.content}</p>
                    </div>
                  ))
                )}
              </div>
              
              <div className="p-4 border-t border-slate-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                    placeholder="Type a message..."
                    className="flex-1 bg-slate-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <button
                    onClick={sendChatMessage}
                    disabled={!newMessage.trim()}
                    className="p-2 bg-teal-600 hover:bg-teal-700 rounded-lg text-white disabled:opacity-50"
                  >
                    <PaperPlaneTilt size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call Controls */}
        <div className="bg-slate-800 p-4">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={toggleAudio}
              className={`p-4 rounded-full ${isAudioEnabled ? 'bg-slate-700 hover:bg-slate-600' : 'bg-red-600 hover:bg-red-700'} text-white`}
              title={isAudioEnabled ? 'Mute' : 'Unmute'}
            >
              {isAudioEnabled ? <Microphone size={24} /> : <MicrophoneSlash size={24} />}
            </button>
            
            <button
              onClick={toggleVideo}
              className={`p-4 rounded-full ${isVideoEnabled ? 'bg-slate-700 hover:bg-slate-600' : 'bg-red-600 hover:bg-red-700'} text-white`}
              title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
            >
              <VideoCamera size={24} weight={isVideoEnabled ? 'regular' : 'slash'} />
            </button>
            
            <button
              onClick={toggleScreenShare}
              className={`p-4 rounded-full ${isScreenSharing ? 'bg-teal-600' : 'bg-slate-700 hover:bg-slate-600'} text-white`}
              title="Share screen"
            >
              <Monitor size={24} />
            </button>
            
            {user?.role !== 'patient' && (
              <button
                onClick={toggleRecording}
                className={`p-4 rounded-full ${isRecording ? 'bg-red-600' : 'bg-slate-700 hover:bg-slate-600'} text-white`}
                title={isRecording ? 'Stop recording' : 'Start recording'}
              >
                {isRecording ? <Stop size={24} /> : <Record size={24} />}
              </button>
            )}
            
            {/* Live Transcription Button - Provider/CMA only */}
            {(user?.role === 'provider' || user?.role === 'cma') && activeCall?.type === 'telehealth' && (
              <button
                onClick={isTranscribing ? stopLiveTranscription : startLiveTranscription}
                className={`p-4 rounded-full ${isTranscribing ? 'bg-purple-600 animate-pulse' : 'bg-slate-700 hover:bg-slate-600'} text-white`}
                title={isTranscribing ? 'Stop transcription' : 'Start live transcription'}
                data-testid="transcription-btn"
              >
                <Waveform size={24} />
              </button>
            )}
            
            {/* Toggle Transcription Panel */}
            {(user?.role === 'provider' || user?.role === 'cma') && (liveTranscript || clinicalNotes) && (
              <button
                onClick={() => setShowTranscription(!showTranscription)}
                className={`p-4 rounded-full ${showTranscription ? 'bg-teal-600' : 'bg-slate-700 hover:bg-slate-600'} text-white`}
                title="Toggle transcription panel"
              >
                <NotePencil size={24} />
              </button>
            )}
            
            <button
              onClick={leaveCall}
              className="p-4 rounded-full bg-red-600 hover:bg-red-700 text-white"
              title="Leave call"
            >
              <PhoneDisconnect size={24} />
            </button>
            
            {(activeCall.created_by === user?.id || user?.role === 'admin') && (
              <button
                onClick={endCall}
                className="px-4 py-2 bg-red-700 hover:bg-red-800 rounded-lg text-white text-sm flex items-center gap-2"
                title="End call for everyone"
              >
                <Phone size={18} />
                End Call
              </button>
            )}
          </div>
          
          {/* Live Transcription Panel */}
          {showTranscription && (liveTranscript || clinicalNotes) && (
            <div className="absolute bottom-28 right-4 w-96 max-h-64 bg-slate-900/95 rounded-xl border border-slate-700 shadow-xl overflow-hidden">
              <div className="p-3 border-b border-slate-700 flex items-center justify-between">
                <h4 className="text-white font-medium text-sm flex items-center gap-2">
                  <Waveform size={16} className={isTranscribing ? 'text-purple-400 animate-pulse' : 'text-teal-400'} />
                  {isTranscribing ? 'Live Transcription' : 'Clinical Notes'}
                </h4>
                <button onClick={() => setShowTranscription(false)} className="text-slate-400 hover:text-white">
                  <X size={16} />
                </button>
              </div>
              <div className="p-3 max-h-48 overflow-y-auto">
                <p className="text-slate-300 text-sm whitespace-pre-wrap">
                  {clinicalNotes || liveTranscript || 'Waiting for transcription...'}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Visit Summary Preview Modal */}
        {showSummaryPreview && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Review Visit Summary</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Review and edit the summary before sending to {patientInfo?.first_name} {patientInfo?.last_name}
                  </p>
                </div>
                <button
                  onClick={() => confirmEndCallAndSendSummary(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                  title="Skip summary and end call"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
                {/* Reason for Visit */}
                <div>
                  <label className="label-clinical">Reason for Visit</label>
                  <input
                    type="text"
                    value={summaryData.reason_for_visit}
                    onChange={(e) => setSummaryData(prev => ({ ...prev, reason_for_visit: e.target.value }))}
                    className="input-clinical"
                    placeholder="Chief complaint"
                  />
                </div>
                
                {/* Vitals (Display only) */}
                {summaryData.vitals && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <label className="label-clinical">Vitals</label>
                    <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                      {summaryData.vitals.blood_pressure && (
                        <div><span className="text-slate-500">BP:</span> {summaryData.vitals.blood_pressure}</div>
                      )}
                      {summaryData.vitals.temperature && (
                        <div><span className="text-slate-500">Temp:</span> {summaryData.vitals.temperature}°F</div>
                      )}
                      {summaryData.vitals.pulse && (
                        <div><span className="text-slate-500">Pulse:</span> {summaryData.vitals.pulse} bpm</div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Assessment */}
                <div>
                  <label className="label-clinical">Assessment (from transcription)</label>
                  <textarea
                    value={summaryData.assessment}
                    onChange={(e) => setSummaryData(prev => ({ ...prev, assessment: e.target.value }))}
                    className="input-clinical h-32"
                    placeholder="Provider&apos;s assessment notes"
                  />
                </div>
                
                {/* Lab Orders */}
                <div>
                  <label className="label-clinical">Lab Orders</label>
                  <input
                    type="text"
                    value={summaryData.lab_orders}
                    onChange={(e) => setSummaryData(prev => ({ ...prev, lab_orders: e.target.value }))}
                    className="input-clinical"
                    placeholder="e.g., CBC, BMP, Lipid Panel"
                  />
                </div>
                
                {/* Imaging Orders */}
                <div>
                  <label className="label-clinical">Imaging Orders</label>
                  <input
                    type="text"
                    value={summaryData.imaging_orders}
                    onChange={(e) => setSummaryData(prev => ({ ...prev, imaging_orders: e.target.value }))}
                    className="input-clinical"
                    placeholder="e.g., Chest X-Ray, MRI"
                  />
                </div>
                
                {/* Referrals */}
                <div>
                  <label className="label-clinical">Referrals</label>
                  <input
                    type="text"
                    value={summaryData.referrals}
                    onChange={(e) => setSummaryData(prev => ({ ...prev, referrals: e.target.value }))}
                    className="input-clinical"
                    placeholder="e.g., Cardiology consult"
                  />
                </div>
                
                {/* Follow-up Instructions */}
                <div>
                  <label className="label-clinical">Follow-up Instructions</label>
                  <input
                    type="text"
                    value={summaryData.followup_instructions}
                    onChange={(e) => setSummaryData(prev => ({ ...prev, followup_instructions: e.target.value }))}
                    className="input-clinical"
                    placeholder="e.g., Follow-up in 2 weeks"
                  />
                </div>
                
                {/* Additional Notes */}
                <div>
                  <label className="label-clinical">Additional Notes</label>
                  <textarea
                    value={summaryData.additional_notes}
                    onChange={(e) => setSummaryData(prev => ({ ...prev, additional_notes: e.target.value }))}
                    className="input-clinical h-20"
                    placeholder="Any additional information for the patient"
                  />
                </div>
              </div>
              
              <div className="p-6 border-t border-slate-200 flex justify-between">
                <button
                  onClick={() => confirmEndCallAndSendSummary(false)}
                  className="btn-outline"
                  disabled={sendingSummary}
                >
                  Skip & End Call
                </button>
                <button
                  onClick={() => confirmEndCallAndSendSummary(true)}
                  className="btn-teal flex items-center gap-2"
                  disabled={sendingSummary}
                  data-testid="send-summary-btn"
                >
                  {sendingSummary ? (
                    <>
                      <div className="spinner-sm" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <EnvelopeSimple size={18} />
                      Send Summary & End Call
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Room List UI
  return (
    <div className="p-8" data-testid="video-calls-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Video Calls
          </h1>
          <p className="text-slate-500">Telehealth consultations and staff meetings</p>
        </div>
        
        {user?.role !== 'patient' && (
          <button
            onClick={() => { setShowCreateModal(true); loadStaffAndPatients(); }}
            className="btn-teal flex items-center gap-2"
            data-testid="create-room-btn"
          >
            <Plus size={18} />
            New Video Call
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-6">
        <button
          onClick={() => setActiveTab('rooms')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'rooms'
              ? 'border-teal-500 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <VideoCamera size={16} className="inline mr-2" />
          Active Rooms
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'history'
              ? 'border-teal-500 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Clock size={16} className="inline mr-2" />
          Call History
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <ArrowClockwise size={32} className="animate-spin text-teal-500" />
        </div>
      ) : (
        <>
          {/* Active Rooms */}
          {activeTab === 'rooms' && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rooms.length === 0 ? (
                <div className="col-span-full card-clinical p-12 text-center">
                  <VideoCamera size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No active video rooms</p>
                  {user?.role !== 'patient' && (
                    <button
                      onClick={() => { setShowCreateModal(true); loadStaffAndPatients(); }}
                      className="mt-4 btn-teal"
                    >
                      Create a Room
                    </button>
                  )}
                </div>
              ) : (
                rooms.map(room => (
                  <div key={room.id} className="card-clinical p-4" data-testid={`room-card-${room.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">{room.room_name}</h3>
                        <p className="text-sm text-slate-500">
                          {room.room_type === 'telehealth' ? 'Telehealth' : 'Staff Meeting'}
                        </p>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                        Active
                      </span>
                    </div>
                    
                    <div className="text-sm text-slate-500 mb-3">
                      <p className="flex items-center gap-1">
                        <UserCircle size={14} />
                        Created by {room.created_by_name}
                      </p>
                      {room.scheduled_time && (
                        <p className="flex items-center gap-1 mt-1">
                          <Calendar size={14} />
                          {new Date(room.scheduled_time).toLocaleString()}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex gap-2 text-xs text-slate-400 mb-4">
                      {room.enable_recording && <span className="flex items-center gap-1"><Record size={12} /> Recording</span>}
                      {room.waiting_room_enabled && <span className="flex items-center gap-1"><Clock size={12} /> Waiting Room</span>}
                    </div>
                    
                    <button
                      onClick={() => joinRoom(room)}
                      className="w-full btn-teal flex items-center justify-center gap-2"
                      data-testid={`join-room-${room.id}`}
                    >
                      <Phone size={18} />
                      Join Call
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Call History */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              {history.length === 0 ? (
                <div className="card-clinical p-12 text-center">
                  <Clock size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No call history</p>
                </div>
              ) : (
                history.map(room => (
                  <div key={room.id} className="card-clinical p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">{room.room_name}</h3>
                      <p className="text-sm text-slate-500">
                        {room.room_type === 'telehealth' ? 'Telehealth' : 'Staff Meeting'} • 
                        {room.is_active ? ' Active' : ' Ended'}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(room.created_at).toLocaleString()}
                      </p>
                    </div>
                    
                    {room.is_active && (
                      <button
                        onClick={() => joinRoom(room)}
                        className="btn-teal text-sm"
                      >
                        Rejoin
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Create Room Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Create Video Room</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="label-clinical">Room Name *</label>
                <input
                  type="text"
                  value={newRoom.room_name}
                  onChange={(e) => setNewRoom({ ...newRoom, room_name: e.target.value })}
                  className="input-clinical"
                  placeholder="e.g., Consultation with John Doe"
                  data-testid="room-name-input"
                />
              </div>

              <div>
                <label className="label-clinical">Room Type</label>
                <select
                  value={newRoom.room_type}
                  onChange={(e) => setNewRoom({ ...newRoom, room_type: e.target.value })}
                  className="input-clinical"
                >
                  <option value="telehealth">Telehealth (Provider-Patient)</option>
                  <option value="staff">Staff Meeting</option>
                </select>
              </div>

              <div>
                <label className="label-clinical">Invite Participants</label>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
                  {newRoom.room_type === 'telehealth' ? (
                    <>
                      <p className="text-xs text-slate-500 mb-2">Patients</p>
                      {patients.map(p => (
                        <label key={p.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newRoom.participant_ids.includes(p.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewRoom({ ...newRoom, participant_ids: [...newRoom.participant_ids, p.id] });
                              } else {
                                setNewRoom({ ...newRoom, participant_ids: newRoom.participant_ids.filter(id => id !== p.id) });
                              }
                            }}
                            className="rounded text-teal-600"
                          />
                          <span className="text-sm">{p.first_name} {p.last_name}</span>
                        </label>
                      ))}
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-slate-500 mb-2">Staff</p>
                      {staffList.map(s => (
                        <label key={s.id} className="flex items-center gap-2 p-1 hover:bg-slate-50 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newRoom.participant_ids.includes(s.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewRoom({ ...newRoom, participant_ids: [...newRoom.participant_ids, s.id] });
                              } else {
                                setNewRoom({ ...newRoom, participant_ids: newRoom.participant_ids.filter(id => id !== s.id) });
                              }
                            }}
                            className="rounded text-teal-600"
                          />
                          <span className="text-sm">{s.first_name} {s.last_name} ({s.role})</span>
                        </label>
                      ))}
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="label-clinical">Schedule (Optional)</label>
                <input
                  type="datetime-local"
                  value={newRoom.scheduled_time}
                  onChange={(e) => setNewRoom({ ...newRoom, scheduled_time: e.target.value })}
                  className="input-clinical"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newRoom.enable_recording}
                    onChange={(e) => setNewRoom({ ...newRoom, enable_recording: e.target.checked })}
                    className="rounded text-teal-600"
                  />
                  <span className="text-sm">Enable Recording</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newRoom.waiting_room_enabled}
                    onChange={(e) => setNewRoom({ ...newRoom, waiting_room_enabled: e.target.checked })}
                    className="rounded text-teal-600"
                  />
                  <span className="text-sm">Enable Waiting Room</span>
                </label>
              </div>

              <button
                onClick={createRoom}
                className="w-full btn-teal"
                data-testid="create-room-submit"
              >
                Create Room
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
